import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { FlexBox } from 'components/Containers/FlexBox';
import { ThemePalette } from 'mui.theme';
import { Typography, Box } from '@mui/material';
import { processMaintenanceData } from './utils';
import { capitalizeFirstLetter } from 'utils/helpers/string.manipulation';
import { convertStdDateString } from 'utils/helpers/dateTime';
import useAppTheme from 'hooks/useAppTheme';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const colors = [
  '#42C3FF', // sky blue
  '#2167B2', // deep blue
  '#C3AA67', // muted mustard
  '#F6E58D', // light yellow
  '#D9A92E', // golden yellow
  '#3D606E', // muted teal
  '#6ADBC7', // mint turquoise
  '#88A8C9', // dusty blue-gray
  '#C5B59D', // soft khaki
  '#B7C6C3', // pale silver
  '#F2D58D', // warm sand

  '#7B98A5', // slate blue
  '#5C7D89', // deeper slate-teal
  '#4AD8C9', // soft aqua
  '#8ac2f2', // pastel blue
  '#D6C29F', // warm beige
  '#A1D5F3', // powder blue
  '#A8B2B6', // soft gray
];

const yokohamaColors = [
  '#E21E31',
  '#58BF6A',
  '#D9A92E',
  '#F6E58D',
  '#8A92A0',
  '#C3AA67',
  '#EB5757',
  '#B4B4B4',
  '#FFA403',
  '#009161',
];

const CustomTooltip = ({ active, payload, type, enableDarkTheme }: any) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const sortedPayload = useMemo(() => {
    if (!payload?.length) return [];
    return [...payload].sort((a: any, b: any) => b.value - a.value);
  }, [payload]);

  if (!active || !sortedPayload.length) return null;

  const { sortKey } = sortedPayload[0].payload;
  const stringToDisplay = convertStdDateString(sortKey, 'MMM YYYY');
  const total = sortedPayload.reduce(
    (sum: number, entry: any) => sum + (entry.value as number),
    0
  );

  const textColor = palette.textPrimary;

  return (
    <div
      style={{
        backgroundColor: enableDarkTheme
          ? 'rgba(30, 40, 60, 0.9)'
          : 'rgba(245, 245, 245, 0.95)',
        border: `1px solid ${enableDarkTheme ? '#3C4A65' : '#ccc'}`,
        padding: '8px 12px',
        borderRadius: '6px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        fontSize: '12px',
      }}
    >
      <p style={{ margin: 0, fontWeight: 'bold', color: textColor }}>
        {stringToDisplay}
      </p>

      {sortedPayload.map((entry: any, i: number) => (
        <p
          key={i}
          style={{ margin: '2px 0', color: entry.color, fontSize: '11px' }}
        >
          {`${capitalizeFirstLetter(entry.name)}: ${entry.value}`}
        </p>
      ))}

      <p
        style={{
          marginTop: '4px',
          fontWeight: 'bold',
          borderTop: '1px solid rgba(0,0,0,0.1)',
          paddingTop: '4px',
          color: textColor,
          fontSize: '11px',
        }}
      >
        {t('total')} {total}
      </p>
    </div>
  );
};

const PartsChart = ({
  data,
  selectedPart,
  selectedFrequency,
  selectedAssets,
}: {
  data: any[];
  selectedAssets: string[];
  selectedPart: string[];
  selectedFrequency: string;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [activeSeries, setActiveSeries] = useState<string[]>([]);

  const handleLegendClick = useCallback((dataKey: string) => {
    setActiveSeries((prev) =>
      prev.includes(dataKey)
        ? prev.filter((el) => el !== dataKey)
        : [...prev, dataKey]
    );
  }, []);

  const chartData = useMemo(() => {
    return data.filter((item: any) => {
      const subCategory = item.sub_component_categories.replace(/^\[|\]$/g, '');
      return (
        selectedPart.includes(`${item.components_category} - ${subCategory}`) &&
        selectedAssets.includes(item.asset_id) &&
        item.actions_performed === 'REPLACE'
      );
    });
  }, [data, selectedPart, selectedAssets]);

  const processedData = useMemo(() => {
    return processMaintenanceData(chartData, selectedFrequency.toLowerCase());
  }, [chartData, selectedFrequency]);

  const categoriesCount = processedData.categories.length;
  const maxBarSize = categoriesCount <= 5 ? 80 : undefined;

  const textColor = palette.textPrimary;
  const cursorFill = enableDarkTheme
    ? CLIENT_THEME === 'yokohama'
      ? palette.sectionBackground
      : 'rgba(27, 43, 74, 0.2)'
    : 'rgba(200,200,200,0.3)';
  const gridStroke = palette.cartesianGrid;
  const chartColors = CLIENT_THEME === 'yokohama' ? yokohamaColors : colors;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <Typography
          sx={{
            color: textColor,
            fontSize: '14px',
            paddingLeft: '10px',
            marginBottom: '20px',
          }}
        >
          {t('frequently_replaced_count_trend')}
        </Typography>
      </Box>

      <FlexBox
        sx={{ justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        {!processedData.data.length ? (
          <Typography
            sx={{
              color: enableDarkTheme
                ? ThemePalette.typography.wheat
                : ThemePalette.typography.black,
            }}
          >
            {t('no_data_for_selected_filter')}
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={processedData.data}
              margin={{ top: 15, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                horizontal
                stroke={gridStroke}
              />
              <XAxis
                dataKey="timeLabel"
                label={{ position: 'insideBottom', offset: -5 }}
                tick={{ fontSize: 11, fill: palette.textSecondary }}
              />
              <YAxis tick={{ fontSize: 11, fill: palette.textSecondary }} />
              <Tooltip
                cursor={{ fill: cursorFill }}
                content={
                  <CustomTooltip
                    type={selectedFrequency}
                    enableDarkTheme={enableDarkTheme}
                  />
                }
              />
              <Legend
                verticalAlign="top"
                wrapperStyle={{ top: -10, left: 25, fontSize: '11px' }}
                iconType="circle"
                formatter={(value) => {
                  const isInactive = activeSeries.includes(value);
                  return (
                    <span
                      style={{
                        textDecoration: isInactive ? 'line-through' : 'none',
                        opacity: isInactive ? 0.5 : 1,
                        cursor: 'pointer',
                      }}
                    >
                      {capitalizeFirstLetter(value)}
                    </span>
                  );
                }}
                onClick={(data) => {
                  data?.dataKey && handleLegendClick(data.dataKey as string);
                }}
              />
              {processedData.categories.map((category, index) => (
                <Bar
                  hide={activeSeries.includes(category)}
                  key={category}
                  dataKey={category}
                  name={category}
                  stackId="a"
                  stroke={chartColors[index % chartColors.length]}
                  fill={chartColors[index % chartColors.length]}
                  maxBarSize={maxBarSize}
                  isAnimationActive
                  animationDuration={300}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </FlexBox>
    </Box>
  );
};

export default PartsChart;
